import { AuthErrorKind } from '@griply/types';
import Typography from '@mui/material/Typography';
import { useRevertEmailChange, useSignInAgain } from 'features/user';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { AuthPageLayout } from 'shared/components/ui/auth-page-layout';
import { Button, Colors, Sizes, Variants } from 'shared/components/ui/button';
import { ResetPasswordForm } from 'shared/components/ui/reset-password-form';
import { QueryParamOptions, useQueryParam } from 'shared/lib/react-router-dom';
import { Paths } from 'shared/routes/paths';
import { ResetPasswordFormFields } from 'shared/types/reset-password-form';

import * as Styled from './revert-email-change.style';

// Opened from the old address: whoever changed it may hold the account, so the restore always sets a new password.
export const RevertEmailChange: React.FC = () => {
  const { t } = useTranslation();
  const [token] = useQueryParam(QueryParamOptions.EmailChangeToken);
  const { revertEmailChange, isLoading, isSuccess, email, error } =
    useRevertEmailChange();
  const signInAgain = useSignInAgain();

  const isDeadLink = !token || error === AuthErrorKind.InvalidLink;
  const isAddressTaken = error === AuthErrorKind.EmailTaken;
  const isReady = !isSuccess && !isDeadLink && !isAddressTaken;

  const onSubmit = ({ password }: ResetPasswordFormFields) =>
    revertEmailChange(token ?? '', password);
  const onSignInAgain = () => {
    void signInAgain();
  };

  return (
    <AuthPageLayout>
      <Typography variant="h1" textAlign="center">
        {t(
          isSuccess
            ? 'auth.revert-email-change.success.title'
            : 'auth.revert-email-change.title',
        )}
      </Typography>

      <Styled.Content>
        {isSuccess && (
          <>
            <Typography variant="body2" textAlign="center">
              {t('auth.revert-email-change.success.message', { email })}
            </Typography>
            <Button
              variant={Variants.Contained}
              color={Colors.Primary}
              size={Sizes.Medium}
              onClick={onSignInAgain}
            >
              {t('forms.reset-password.back-to-login.label')}
            </Button>
          </>
        )}

        {(isDeadLink || isAddressTaken) && (
          <>
            <Typography variant="body2" textAlign="center">
              {t(
                isAddressTaken
                  ? 'auth.revert-email-change.errors.email-taken'
                  : 'auth.errors.invalid-link',
              )}
            </Typography>
            <Styled.Anchor to={Paths.Login}>
              {t('forms.reset-password.back-to-login.label')}
            </Styled.Anchor>
          </>
        )}

        {isReady && (
          <>
            <Typography variant="body2">
              {t('auth.revert-email-change.description')}
            </Typography>
            <ResetPasswordForm
              onSubmit={onSubmit}
              isLoading={isLoading}
              error={error}
            />
          </>
        )}
      </Styled.Content>
    </AuthPageLayout>
  );
};
